<template>
  <nav class="search-navigation" aria-label="Search menu">
    <ul
      v-accessible-menubar
      class="search-navigation__list"
      aria-label="Search menu"
      role="menubar"
    >
      <li
        v-for="vertical in verticals"
        :key="vertical.id"
        :class="{
          'search-navigation__item': true,
          'search-navigation__item--highlight': isShoppingButNotCurrent(vertical),
        }"
        :data-test-id="`search-navigation-item-${vertical.id}`"
        role="none"
      >
        <ETab
          :url="getUrl(vertical.path)"
          :icon="vertical.id"
          :highlighted="isCurrentVertical(vertical.path)"
          :track-id="`nav_${vertical.id}`"
          :data-test-id="`search-navigation-${vertical.id}`"
          bold-highlight-text
        >
          {{ $t(`common.header.verticals.${vertical.id}`) }}
          <EIcon
            v-if="isShoppingButNotCurrent(vertical)"
            class="search-navigation__highlight-star"
            size="s"
            icon="highlight-star"
          />
        </ETab>
      </li>
      <li class="search-navigation__item" role="none">
        <ETab
          :dropdown-title="$t('common.header.dropdowns.maps.title')"
          icon="maps"
          data-test-id="search-navigation-maps"
          data-track-id="nav_maps"
        >
          {{ this.$t('common.header.verticals.maps') }}
          <template #dropdown>
            <nav>
              <EList>
                <EListItem v-if="ecosiaMapsShown" role="none">
                  <a
                    :href="getUrl('/map')"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-ecosia"
                    data-track-id="maps_ecosia"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-ecosia"
                    />
                    Ecosia {{ $t('common.header.verticals.maps') }}
                  </a>
                </EListItem>
                <EListItem v-if="bingMapsShown" role="none">
                  <a
                    :href="bingMapsUrl"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-bing"
                    data-track-id="maps_bing"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-bing"
                      size="m"
                    />
                    Bing
                  </a>
                </EListItem>
                <EListItem v-if="treedayShown" role="none">
                  <a
                    :href="treedayUrl"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-treeday"
                    data-track-id="maps_treeday"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-treeday"
                      size="m"
                    />
                    Treeday
                  </a>
                </EListItem>
                <EListItem role="none">
                  <a
                    :href="googleMapsUrl"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-google"
                    data-track-id="maps_google"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-google-maps"
                      size="s"
                    />
                    Google Maps
                  </a>
                </EListItem>
              </EList>
            </nav>
          </template>
        </ETab>
      </li>
      <li class="search-navigation__item" role="none">
        <ETab
          :dropdown-title="$t('common.header.dropdowns.searchoptions.title')"
          icon="more"
          data-test-id="search-navigation-more"
          data-track-id="nav_more"
        >
          {{ this.$t('common.header.searchoptions.more') }}
          <template #dropdown>
            <nav>
              <EList>
                <EListItem
                  v-if="!shoppingLink"
                  :content-style="true"
                  role="none"
                >
                  <a
                    :href="shoppingUrl"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                    tabindex="0"
                    class="search-navigation__dropdown-link"
                    data-test-id="shopping-link"
                    data-track-id="more_shopping"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="shopping"
                      size="s"
                    />
                    {{ $t('common.header.searchoptions.shopping') }}
                  </a>
                </EListItem>
                <EListItem
                  v-for="shortcut in shortcuts"
                  :key="shortcut.title"
                  :content-style="true"
                  role="none"
                >
                  <a
                    :href="shortcut.url"
                    class="search-navigation__dropdown-link"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                    tabindex="0"
                    data-test-id="search-tag-link"
                    :data-track-id="`more_${shortcut.title}`"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      :icon="shortcut.icon"
                      size="m"
                    />
                    {{ $t(`common.header.searchoptions.${shortcut.title}`) }}
                  </a>
                </EListItem>
              </EList>
            </nav>
          </template>
        </ETab>
      </li>
      <div class="search-navigation__divider" />
      <li
        v-if="filtersToggle"
        class="search-navigation__item search-navigation__item--align-right"
        role="none"
      >
        <ETab
          ref="filters"
          :highlight-bar="false"
          :dropdown-title="$t('common.header.dropdowns.filters.title')"
          :highlighted="filtersShown"
          dropdown-size="l"
          dropdown-side="right"
          data-test-id="search-navigation-filters"
          data-track-id="nav_filters"
          @dropdown-visibility-change="onFiltersVisibilityChange"
        >
          {{ this.$t('common.header.dropdowns.filters.title') }}
        </ETab>
      </li>
      <li class="search-navigation__item search-navigation__item--align-right" role="none">
        <ETab
          url="/settings"
          :highlight-bar="false"
          :dropdown-title="$t('common.header.dropdowns.settings.title')"
          :desktop-padding="false"
          dropdown-size="l"
          dropdown-side="right"
          data-test-id="search-navigation-settings"
          data-track-id="nav_settings"
          class="search-navigation__settings-tab"
        >
          <EButton
            class="search-navigation__settings-button"
            variant="bare"
            size="s"
          >
            {{ $t('common.header.menu.settings') }}
          </EButton>
        </ETab>
      </li>
    </ul>
  </nav>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import { getHashParam, setHashParam, deleteHashParam } from '~~/common/js/universal/hash-params';
import { TREEDAY_URL } from '~~/common/js/constants/urls';
import { searchShortcuts } from '~~/common/js/universal/search-shortcuts/dropdown';
import { addTagToQuery } from '~~/common/js/universal/search-shortcuts/utils';
import { getBingMapsSearchUrl, getGoogleMapsSearchUrl } from '~~/common/js/universal/url-utils';
import { getUrlQuery } from '~~/common/js/universal/search';

import EButton from '~~/common/vue/components/button';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';
import ETab from '~~/common/vue/components/tab';
import { FILTERS_KEY } from '~~/search/common/js/universal/hash-params';

const shoppingVertical = {
  id: 'shopping',
  path: '/shopping',
};
export const verticals = [{
  id: 'web',
  path: '/search',
}, {
  id: 'images',
  path: '/images',
}, {
  id: 'news',
  path: '/news',
}, {
  id: 'videos',
  path: '/videos',
}];

/**
 * Allows the user to navigate between verticals and other search options.
 * Requires the `user` Vuex module.
 */
export default {
  name: 'ESearchNavigation',
  components: {
    EButton,
    EIcon,
    EList,
    EListItem,
    ETab,
  },
  props: {
    filtersToggle: {
      type: Boolean,
      default: false,
    },
    shoppingLink: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    focusedElement: null,
  }),
  computed: {
    ...mapState('search', ['filtersShown']),
    query() {
      return this.$route.query.originalQuery?.toString() || this.$route.query.q?.toString() || '';
    },
    verticals() {
      if (this.shoppingLink) {
        const atIndex = 2;
        return [...verticals.slice(0, atIndex), shoppingVertical, ...verticals.slice(atIndex)];
      }
      return verticals;
    },
    bingMapsUrl() {
      return getBingMapsSearchUrl(this.query);
    },
    googleMapsUrl() {
      return getGoogleMapsSearchUrl(this.query);
    },
    treedayUrl() {
      return TREEDAY_URL;
    },
    shortcuts() {
      const { path } = this.verticals.find(({ id }) => id === 'web');
      return searchShortcuts.map(({ title, icon, tag }) => {
        const fromQuery = getUrlQuery(this.$route.query, this.$store.state.user);
        const query = { ...fromQuery, q: addTagToQuery(this.query, tag) };
        const url = this.$router.resolve({ path, query }).href;
        return { title, icon, url };
      });
    },
    treedayShown() {
      // intentionally deactivated for the time being (see CS-476)
      return false;
    },
    bingMapsShown() {
      return !this.treedayShown;
    },
    ecosiaMapsShown() {
      // intentionally deactivated for the time being (see CS-476)
      return false;
    },
    shoppingUrl() {
      return this.getUrl(shoppingVertical.path);
    },
  },
  beforeMount() {
    // on page load, the filters should be open if the hash param is set to true
    if (getHashParam(FILTERS_KEY) === 'true') {
      this.setFiltersShown(true);
    }
  },
  methods: {
    ...mapMutations('search', ['setFiltersShown']),
    isCurrentVertical(path) {
      return path === this.$route.path;
    },
    isShoppingVertical(id) {
      return id === shoppingVertical.id;
    },
    isShoppingButNotCurrent({ id, path }) {
      return this.isShoppingVertical(id) && !this.isCurrentVertical(path);
    },
    getUrl(path) {
      const query = getUrlQuery(this.$route.query, this.$store.state.user);
      return this.$router.resolve({ path, query }).href;
    },
    onFiltersVisibilityChange(isVisible) {
      this.setFiltersShown(isVisible);

      if (isVisible) {
        setHashParam(FILTERS_KEY, 'true');
      } else {
        deleteHashParam(FILTERS_KEY);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-navigation {
  @extend %no-scrollbar;

  overflow: scroll;
  overflow-y: hidden;
  padding: 0 $space-m;

  @include desktop {
    overflow: visible;
    padding: 0;
  }
}

.search-navigation__list {
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: max-content;

  @include desktop {
    display: flex;
    width: $page-container-width-desktop - $space-m;
  }

  &:focus {
    outline: none;
  }
}

.search-navigation__item {
  align-items: flex-end;
  display: inline-flex;
  flex-shrink: 0;
  height: $space-4l;

  @include desktop {
    height: $space-3l;
  }

  & + & {
    margin-left: $space-s;

    @include tablet {
      margin-left: $space-s;
    }
  }
}

.search-navigation__item.search-navigation__item--align-right {
  @include desktop {
    margin-left: auto;
    margin-right: $space-s;

    &:last-of-type {
      margin-right: $space-2s;
    }

    & + & {
      margin-left: 0;
    }
  }
}

.search-navigation__dropdown-link {
  align-items: center;
  display: flex;
  font-size: $font-m;
  height: $space-l;
  white-space: nowrap;
  padding: 0 $space-s;
  min-height: $tappable-area-size-mobile;

  @include desktop {
    font-size: $font-m;
    min-height: $space-3l;
  }

  &:focus {
    outline: none; // outline already provided by ListItem
  }
}

.search-navigation__dropdown-icon {
  margin-right: $space-1s;
  // we may want to have a small-size icon in the dropdown
  // but for layout reasons we want it to occupy the space of a medium-size
  min-width: $icon-size-m;
}

.search-navigation__dropdown-label {
  margin-left: $space-1s;
}

.search-navigation__divider {
  background: var(--color-decorative-border-1);
  border: none;
  display: inline-block;
  flex-shrink: 0;
  height: $space-m;
  margin: 0 0 0 $space-s;
  vertical-align: middle;
  width: 1px;

  @include tablet {
    margin: 0 $space-m 0;
  }

  @include desktop {
    display: none;
  }
}

.search-navigation__highlight-star {
  color: var(--color-brand-primary);
  margin-left: $space-2s;
}

.search-navigation__settings-tab {
  max-height: $tappable-area-size-mobile;
}

.search-navigation__settings-button {
  @include desktop {
    // special shift to align the button with the other tabs
    position: relative;
    top: $space-2s + $space-2s / 2;
  }
}

.search-navigation__item--highlight {
  background-color: var(--color-highlight-primary);
  border-top-left-radius: $border-radius-m;
  border-top-right-radius: $border-radius-m;
  padding-left: $space-1s;
  padding-right: $space-2s;
}
</style>
